import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { HelpCircle, Search, Loader2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { instanciaService } from '../../services/instanciaService';
import MetaManualSetupGuide from './MetaManualSetupGuide';
import HintTooltip from '../common/HintTooltip';

// Textos de ajuda de cada campo ficam no i18n (instanceForm.help.<campo>);
// aqui só o link de cada um.
const FIELD_HELP_LINKS: Record<string, string> = {
  phoneNumberId: 'https://business.facebook.com/settings/whatsapp-accounts',
  wabaId: 'https://business.facebook.com/settings/whatsapp-accounts',
  accessToken: 'https://business.facebook.com/settings/system-users',
  appSecret: 'https://developers.facebook.com/apps/',
  displayName: '',
};

interface MetaInstanceFormProps {
  formData: any;
  setFormData: (data: any) => void;
  disabled?: boolean;
  uazapiInstances?: Array<{ id: string; nome: string; statusConexao: string }>;
}

const HelpPopover: React.FC<{ field: string }> = ({ field }) => {
  const { t } = useTranslation();
  if (!(field in FIELD_HELP_LINKS)) return null;
  const linkUrl = FIELD_HELP_LINKS[field];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-center h-5 w-5 text-muted-foreground hover:text-foreground transition-colors"
          tabIndex={-1}
        >
          <HelpCircle className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 text-sm" side="top" align="center">
        <div className="space-y-2">
          <p className="font-medium">{t(`instanceForm.help.${field}.description`)}</p>
          <div className="bg-muted rounded-md p-2">
            <p className="text-xs text-muted-foreground font-medium mb-1">{t('instanceForm.whereToFind')}</p>
            <p className="text-xs">{t(`instanceForm.help.${field}.whereToFind`)}</p>
          </div>
          {linkUrl && (
            <a
              href={linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
            >
              {t(`instanceForm.help.${field}.linkLabel`)} ↗
            </a>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const MetaInstanceForm: React.FC<MetaInstanceFormProps> = ({ formData, setFormData, disabled, uazapiInstances = [] }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const handleChange = (field: string, value: string | null) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  // Descoberta automática de WABA/número a partir do Access Token já colado
  // pelo usuário — evita ter que copiar WABA ID e Phone Number ID à mão de
  // duas telas diferentes do Meta Business Suite.
  const [discovering, setDiscovering] = useState(false);
  const [discoveryError, setDiscoveryError] = useState<string | null>(null);
  type DiscoveredWaba = { id: string; name?: string | null; business?: { id: string; name?: string | null } | null; phoneNumbers: Array<{ id: string; display_phone_number?: string; verified_name?: string }> };
  const [discoveredWabas, setDiscoveredWabas] = useState<DiscoveredWaba[] | null>(null);
  const [selectedWabaId, setSelectedWabaId] = useState('');
  const [selectedPhoneId, setSelectedPhoneId] = useState('');

  const handleDiscover = async () => {
    if (!formData.accessToken) {
      toast({ title: t('instanceForm.tokenRequiredTitle'), description: t('instanceForm.tokenRequiredDesc'), variant: 'destructive' });
      return;
    }
    setDiscovering(true);
    setDiscoveryError(null);
    try {
      const { data } = await instanciaService.descobrirMetaWaba(formData.accessToken);
      const wabas: DiscoveredWaba[] = data.wabas?.length ? data.wabas : (data.wabaId ? [{ id: data.wabaId, name: null, phoneNumbers: data.phoneNumbers || [] }] : []);

      if (wabas.length === 0 || wabas.every((w) => w.phoneNumbers.length === 0)) {
        setDiscoveryError(t('instanceForm.noWabaFound'));
        return;
      }

      const firstWaba = wabas.find((w) => w.phoneNumbers.length > 0) || wabas[0];
      const firstPhone = firstWaba.phoneNumbers[0];

      // Sempre pede confirmação (portfólio › WABA › número), mesmo com uma só
      // opção: o usuário vê o que o token acessa antes de preencher os campos.
      setDiscoveredWabas(wabas);
      setSelectedWabaId(firstWaba.id);
      setSelectedPhoneId(firstPhone?.id || '');
      toast({ title: t('instanceForm.wabaFoundTitle'), description: t('instanceForm.confirmFound') });
    } catch (err: any) {
      const msg = err?.response?.data?.message || t('instanceForm.discoverError');
      setDiscoveryError(msg);
    } finally {
      setDiscovering(false);
    }
  };

  const selectedWabaOption = discoveredWabas?.find((w) => w.id === selectedWabaId) || null;

  const handleUseDiscovered = () => {
    if (!selectedWabaId || !selectedPhoneId) return;
    handleChange('wabaId', selectedWabaId);
    handleChange('phoneNumberId', selectedPhoneId);
    setDiscoveredWabas(null);
  };

  return (
    <div className="grid gap-4 py-4">
      <MetaManualSetupGuide defaultOpen={!formData.wabaId && !formData.accessToken && !formData.phoneNumberId} />

      {/* WhatsApp Business Account ID */}
      <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-1.5 sm:gap-4">
        <div className="flex items-center sm:justify-end gap-1">
          <Label htmlFor="wabaId" className="sm:text-right dark:text-gray-200">
            {t('instanceForm.wabaId')} <span className="text-red-500">*</span>
          </Label>
          <HelpPopover field="wabaId" />
        </div>
        <Input
          id="wabaId"
          name="meta-waba-id"
          autoComplete="off"
          value={formData.wabaId || ''}
          onChange={(e) => handleChange('wabaId', e.target.value)}
          className="sm:col-span-3 dark:bg-gray-200"
          placeholder={t('instanceForm.idPlaceholder')}
          disabled={disabled}
        />
      </div>

      {/* Access Token */}
      <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-1.5 sm:gap-4">
        <div className="flex items-center sm:justify-end gap-1">
          <Label htmlFor="accessToken" className="sm:text-right dark:text-gray-200">
            {t('instanceForm.accessToken')} <span className="text-red-500">*</span>
          </Label>
          <HelpPopover field="accessToken" />
        </div>
        <Input
          id="accessToken"
          name="meta-access-token"
          autoComplete="new-password"
          type="password"
          value={formData.accessToken || ''}
          onChange={(e) => handleChange('accessToken', e.target.value)}
          className="sm:col-span-3 dark:bg-gray-200"
          placeholder={t('instanceForm.accessTokenPlaceholder')}
          disabled={disabled}
        />
      </div>

      {/* Descoberta automática de WABA + Número a partir do Access Token */}
      <div className="grid grid-cols-1 sm:grid-cols-4 items-start gap-1.5 sm:gap-4">
        <div className="hidden sm:block" />
        <div className="sm:col-span-3 space-y-2 rounded-md border border-dashed dark:border-gray-600 p-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              {t('instanceForm.discoverIntro')}
            </p>
            <HintTooltip text={t('instanceForm.discoverHint')} className="inline-flex shrink-0">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0 h-8 text-xs"
                onClick={handleDiscover}
                disabled={disabled || discovering || !formData.accessToken}
              >
                {discovering ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Search className="h-3.5 w-3.5 mr-1" />}
                {t('instanceForm.discoverButton')}
              </Button>
            </HintTooltip>
          </div>

          {discoveryError && (
            <p className="text-xs text-red-500">{discoveryError}</p>
          )}

          {discoveredWabas && (
            <div className="space-y-2 pt-1">
              <p className="text-xs font-medium">{t('instanceForm.pickerTitle')}</p>
              <div className="grid gap-1.5">
                <Label className="text-xs dark:text-gray-300">{t('instanceForm.wabaLabel')}</Label>
                <Select
                  value={selectedWabaId}
                  onValueChange={(value) => {
                    setSelectedWabaId(value);
                    const waba = discoveredWabas.find((w) => w.id === value);
                    setSelectedPhoneId(waba?.phoneNumbers[0]?.id || '');
                  }}
                >
                  <SelectTrigger className="h-8 text-xs dark:bg-gray-700 dark:text-white">
                    <SelectValue placeholder={t('instanceForm.selectWaba')} />
                  </SelectTrigger>
                  <SelectContent>
                    {discoveredWabas.map((w) => (
                      <SelectItem key={w.id} value={w.id}>
                        {w.business?.name ? `${w.business.name} › ` : ''}{w.name || `WABA ${w.id}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label className="text-xs dark:text-gray-300">{t('instanceForm.phoneLabel')}</Label>
                <Select
                  value={selectedPhoneId}
                  onValueChange={setSelectedPhoneId}
                  disabled={!selectedWabaOption || selectedWabaOption.phoneNumbers.length === 0}
                >
                  <SelectTrigger className="h-8 text-xs dark:bg-gray-700 dark:text-white">
                    <SelectValue placeholder={t('instanceForm.selectPhone')} />
                  </SelectTrigger>
                  <SelectContent>
                    {(selectedWabaOption?.phoneNumbers || []).map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.display_phone_number || p.id}{p.verified_name ? ` — ${p.verified_name}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setDiscoveredWabas(null)}>{t('common.cancel')}</Button>
                <HintTooltip text={t('instanceForm.useTheseHint')}>
                  <Button type="button" size="sm" className="h-7 text-xs" onClick={handleUseDiscovered} disabled={!selectedWabaId || !selectedPhoneId}>{t('instanceForm.useThese')}</Button>
                </HintTooltip>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Phone Number ID */}
      <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-1.5 sm:gap-4">
        <div className="flex items-center sm:justify-end gap-1">
          <Label htmlFor="phoneNumberId" className="sm:text-right dark:text-gray-200">
            {t('instanceForm.phoneNumberId')} <span className="text-red-500">*</span>
          </Label>
          <HelpPopover field="phoneNumberId" />
        </div>
        <div className="sm:col-span-3 space-y-1">
          <Input
            id="phoneNumberId"
            value={formData.phoneNumberId || ''}
            onChange={(e) => handleChange('phoneNumberId', e.target.value)}
            className="dark:bg-gray-200"
            placeholder={t('instanceForm.idPlaceholder')}
            disabled={disabled}
          />
          <p className="text-xs text-muted-foreground">{t('instanceForm.phoneNumberIdSyncHint')}</p>
        </div>
      </div>

      {/* App Secret */}
      <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-1.5 sm:gap-4">
        <div className="flex items-center sm:justify-end gap-1">
          <Label htmlFor="appSecret" className="sm:text-right dark:text-gray-200">
            {t('instanceForm.appSecret')}
          </Label>
          <HelpPopover field="appSecret" />
        </div>
        <Input
          id="appSecret"
          name="meta-app-secret"
          autoComplete="new-password"
          type="password"
          value={formData.appSecret || ''}
          onChange={(e) => handleChange('appSecret', e.target.value)}
          className="sm:col-span-3 dark:bg-gray-200"
          placeholder={t('instanceForm.appSecretPlaceholder')}
          disabled={disabled}
        />
      </div>

      {/* Display Name */}
      <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-1.5 sm:gap-4">
        <div className="flex items-center sm:justify-end gap-1">
          <Label htmlFor="displayName" className="sm:text-right dark:text-gray-200">
            {t('instanceForm.displayName')}
          </Label>
          <HelpPopover field="displayName" />
        </div>
        <Input
          id="displayName"
          value={formData.displayName || ''}
          onChange={(e) => handleChange('displayName', e.target.value)}
          className="sm:col-span-3 dark:bg-gray-200"
          placeholder={t('instanceForm.displayNamePlaceholder')}
          disabled={disabled}
        />
      </div>

      {/* Instância UAZAPI Vinculada (Híbrido) */}
      {uazapiInstances.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-1.5 sm:gap-4">
          <div className="flex items-center sm:justify-end gap-1">
            <Label htmlFor="instanciaUazapiId" className="sm:text-right dark:text-gray-200">
              {t('instanceForm.linkedUazapiInstance')}
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center justify-center h-5 w-5 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  <HelpCircle className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 text-sm" side="top" align="center">
                <div className="space-y-2">
                  <p className="font-medium">{t('instanceForm.hybridTitle')}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('instanceForm.hybridDesc')}
                  </p>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <div className="sm:col-span-3">
            <Select
              value={formData.instanciaUazapiId || '__none__'}
              onValueChange={(value) => handleChange('instanciaUazapiId', value === '__none__' ? null : value)}
              disabled={disabled}
            >
              <SelectTrigger className="dark:bg-gray-200">
                <SelectValue placeholder={t('instanceForm.noneMetaOnly')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">{t('instanceForm.noneMetaOnly')}</SelectItem>
                {uazapiInstances
                  .filter(u => u.statusConexao === 'connected' || u.statusConexao === 'connecting')
                  .map((uazapi) => (
                    <SelectItem key={uazapi.id} value={uazapi.id}>
                      {uazapi.nome}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetaInstanceForm;
